import React, {
  useContext,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import { useQuery } from "@apollo/client";
import { GET_PREFERENCES } from "./AppPreferencesApi";
import { AuthContext } from "../Auth/AuthContext";
import ThirdPartyProvider from "../ThirdPartyProviders/ThirdPartyProvider";
import {
  AppPreferencesContext,
  userActionsEnum,
} from "./AppPreferencesContext";
import type {
  AppPreferences,
  AppPreferencesContextType,
  UserAction,
} from "../types/AppPreferencesProvider";
import type { AuthState } from "../types/AuthProvider";

interface UserActionEdge {
  node?: {
    id: string;
    name?: string;
  };
}

// Shape of the GET_PREFERENCES query response. Fields are optional because the
// provider reads them defensively via optional chaining.
interface PreferencesResponse {
  preferences?: {
    customer_preferences?: {
      has_findings?: boolean;
      severity_as_priority?: boolean;
      ticket_due_date_enabled?: boolean;
      severity_as_priority_mapping?: string;
      identity_provider?: string;
      password_enabled?: boolean;
      using_new_trends?: boolean;
      use_clickhouse?: boolean;
      use_ai?: boolean;
      account_type?: AppPreferences["accountType"];
      use_plain_unified_resources?: boolean;
      use_resource_attributes?: boolean;
      open_seemplicity_findings?: boolean | null;
    };
    user_preferences?: {
      user_id?: number;
      any_scopes?: boolean;
      role?: string;
      user_actions?: {
        edges?: UserActionEdge[];
      };
    };
  };
}

// Re-exported for backward compatibility — these now live in
// `./AppPreferencesContext` so they can be imported (e.g. by the test/story
// render harness) without triggering this provider's heavy transitive imports.
export { AppPreferencesContext, userActionsEnum };

const RESOURCES_TABLE_VIEW_KEY = "resources-table-view";
const RESOURCES_TABLE_VIEW_RESET_KEY = "resources-table-view-reset-v1";

const resetResourcesTableViewOnce = () => {
  try {
    const hasReset = localStorage.getItem(RESOURCES_TABLE_VIEW_RESET_KEY);
    if (hasReset) {
      return;
    }
    localStorage.removeItem(RESOURCES_TABLE_VIEW_KEY);
    localStorage.setItem(RESOURCES_TABLE_VIEW_RESET_KEY, "true");
  } catch (e) {
    // ignore storage errors (e.g. private mode)
  }
};

interface AppPreferencesProviderProps {
  children: ReactNode;
}

const AppPreferencesProvider: React.FC<AppPreferencesProviderProps> = ({
  children,
}) => {
  const [appPreferences, setAppPreferences] = useState<AppPreferences | null>(
    null
  );

  const {
    authState: { authenticated, username },
  } = useContext(AuthContext) as { authState: AuthState };

  useQuery<PreferencesResponse>(GET_PREFERENCES, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    skip: !authenticated,
    onCompleted: (data) => {
      const dueDateTicketSlaEnabled =
        data?.preferences?.customer_preferences?.ticket_due_date_enabled;
      const userActionsArray =
        data?.preferences?.user_preferences?.user_actions?.edges;
      const userActions: UserAction[] = userActionsArray!.map((action) => {
        return { id: atob(action?.node?.id as string).split(":")?.[1] };
      });

      const hasDatasources = true;
      // data?.preferences?.customer_preferences?.has_datasources; // THIS IS A RELIEF UNTIL BACKEND IS STABLE
      const hasFindings = data?.preferences?.customer_preferences?.has_findings;
      const severityAsPriority =
        data?.preferences?.customer_preferences?.severity_as_priority;
      const hasScopesAllowed = data?.preferences?.user_preferences?.any_scopes;
      const severityAsPriorityMapping = !!severityAsPriority
        ? JSON.parse(
            data?.preferences?.customer_preferences
              ?.severity_as_priority_mapping as string
          )
        : null;
      const samlLoginUrl = data?.preferences?.customer_preferences
        ?.identity_provider
        ? `${import.meta.env.VITE_DOMAIN}/saml/${
            data?.preferences?.customer_preferences?.identity_provider
          }`
        : null;
      const ssoEnforced =
        data?.preferences?.customer_preferences?.password_enabled === false;
      const useNewTrends =
        data?.preferences?.customer_preferences?.using_new_trends;
      const useClickhouse =
        !!data?.preferences?.customer_preferences?.use_clickhouse;

      const useAI = data?.preferences?.customer_preferences?.use_ai;
      const aiPermitted = !useAI
        ? userActions?.some(
            (action) => action.id === userActionsEnum.EDIT_AI_CONFIGURATION
          )
        : true;
      const encourageAi =
        userActions?.some(
          (action) => action.id === userActionsEnum.EDIT_AI_CONFIGURATION
        ) && !useAI;

      const usePlainUnifiedResources =
        data?.preferences?.customer_preferences?.use_plain_unified_resources;

      const useResourceAttributes =
        data?.preferences?.customer_preferences?.use_resource_attributes;

      const openSeemplicityFindings =
        data?.preferences?.customer_preferences?.open_seemplicity_findings ===
        true;

      if (useResourceAttributes) {
        resetResourcesTableViewOnce();
      }

      setAppPreferences(
        (state) =>
          ({
            ...state,
            hasScopesAllowed,
            userActions,
            severityAsPriority,
            hasDatasources,
            hasFindings,
            dueDateTicketSlaEnabled,
            severityAsPriorityMapping,
            samlLoginUrl,
            ssoEnforced,
            useNewTrends,
            useClickhouse,
            useAI,
            aiPermitted,
            encourageAi,
            role: data?.preferences?.user_preferences?.role,
            userId: data?.preferences?.user_preferences?.user_id,
            accountType: data?.preferences?.customer_preferences?.account_type,
            usePlainUnifiedResources,
            useResourceAttributes,
            openSeemplicityFindings,
            // Cast: several `AppPreferences` fields are typed required, but the
            // backend can return undefined/null here; the original JS stored
            // those values verbatim, so we preserve that exactly.
          }) as AppPreferences
      );
    },
  });

  const isUserActionAllowed = useCallback(
    (actionId: string) =>
      appPreferences?.userActions?.some((action) => action.id === actionId),
    [appPreferences?.userActions]
  );

  const isSelfActionAllowed = useCallback(
    (actionId: string, ownerUsername?: string) =>
      isUserActionAllowed(actionId) && ownerUsername === username,
    [isUserActionAllowed, username]
  );

  const appPreferencesAreSet = useMemo(
    () =>
      Boolean(
        appPreferences !== null &&
          Object.keys(appPreferences).length &&
          !!appPreferences?.userActions
      ),
    [appPreferences]
  );

  return (
    <AppPreferencesContext.Provider
      value={
        {
          appPreferences,
          isAllowed: isUserActionAllowed,
          isSelfActionAllowed,
          openSeemplicityFindings: appPreferences?.openSeemplicityFindings,
        } as AppPreferencesContextType
      }
    >
      {appPreferencesAreSet && (
        <ThirdPartyProvider>{children}</ThirdPartyProvider>
      )}
    </AppPreferencesContext.Provider>
  );
};

export default AppPreferencesProvider;
